import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

function StatusIndicator({ status, message }) {
  const Icon = status === "paid" ? CheckCircle2 : status === "checking" ? Loader2 : XCircle;
  const color = status === "paid" ? "text-green-400" : status === "checking" ? "text-gs-neon" : "text-red-400";
  const heading = status === "paid" ? "Ticket cleared" : status === "checking" ? "Box office processing" : "Status";
  return (
    <div className="max-w-md text-center">
      <Icon className={`mx-auto ${color} ${status === "checking" ? "animate-spin" : ""}`} size={48} />
      <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gs-neon mt-8 mb-4">{heading}</p>
      <h1 className="font-display text-4xl tracking-wide">{message}</h1>
    </div>
  );
}

export default function CheckoutReturn() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | unpaid | error | timeout
  const [message, setMessage] = useState("Verifying your payment with Stripe…");

  useEffect(() => {
    document.body.classList.add("theme-mini");
    return () => document.body.classList.remove("theme-mini");
  }, []);

  const handlePaid = useCallback(() => {
    setStatus("paid");
    setMessage("Payment confirmed. Welcome to the theater.");
    setTimeout(() => navigate(`/film/${slug}`), 1500);
  }, [navigate, slug]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setMessage("Missing session id.");
      return undefined;
    }
    if (user === false) {
      navigate(`/login?redirect=/film/${slug}/return?session_id=${sessionId}`);
      return undefined;
    }
    if (!user) return undefined;

    let attempts = 0;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          handlePaid();
          return;
        }
        if (data.status === "expired") {
          setStatus("unpaid");
          setMessage("Your checkout session expired.");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          setMessage("Still processing. Check your library shortly.");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(formatApiError(e));
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, slug, user, navigate, handlePaid]);

  return (
    <div data-testid="checkout-return-page" className="film-grain min-h-screen bg-gs-screen text-white flex items-center justify-center px-6">
      <StatusIndicator status={status} message={message} />
    </div>
  );
}
